import { useEffect, useState, useContext } from "react"
import {
  postGetRedButtonStatus,
  postHighPriorityTruck,
  postSetRedButtonStatus,
} from "@Services/prioritizationService"
import { postSearchEntity } from "@Services/osrmServices"
import { useEnv } from "@Hooks"
import { GlobalContext } from "@Context/context/GlobalState"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"

const usePrioritizationServices = ({
  client,
  loadRedButtonStatus = false,
  afterSuccessAction,
  withTimeout,
  getTrucksAcceptOrderInfo = false,
  getSummaryOrderInfo = false,
  currentStatus
}) => {
  const { t } = useTranslation("global")
  const user = useEnv().multiclient.get_user()
  const tokenService = user?.token
  const { handleErrorSession } = useContext(GlobalContext)
  const [redButtonStatus, setRedButtonStatus] = useState(false)
  const [acceptOrdersInfo, setAcceptOrdersInfo] = useState({})
  const [summaryOrdersInfo, setSummaryOrdersInfo] = useState({})
  const [loadingRedButton, setLoadingRedButton] = useState(false)
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)

  useEffect(() => {
    loadRedButtonStatus && getRedButtonStatusAction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRedButtonStatus])

  useEffect(() => {
    getTrucksAcceptOrderInfo && getTrucksAcceptOrderInfoAction()
    getSummaryOrderInfo && getSummaryOrderInfoAction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTrucksAcceptOrderInfo, getSummaryOrderInfo])

  const onSuccess = (actionLoading) => {
    if (withTimeout) {
      setTimeout(() => {
        Swal.fire(t("Success"), t("done"), "success")
        actionLoading(false)
        afterSuccessAction && afterSuccessAction()
      }, withTimeout)
    } else {
      Swal.fire(t("Success"), t("done"), "success")
      actionLoading(false)
      afterSuccessAction && afterSuccessAction()
    }
  }

  const getRedButtonStatusAction = async () => {
    try {
      setLoadingRedButton(true)
      const response = await postGetRedButtonStatus(tokenService, client, {
        event: {
          name: "get_red_button_status",
          data: {},
        },
      })
      const statusButtonResponse =
        response?.data.integrations[0]?.response?.status
      setRedButtonStatus(statusButtonResponse)
      setLoadingRedButton(false)
    } catch (error) {
      setLoadingRedButton(false)
      handleErrorSession({
        error: true,
        errorCode: error.response.status,
        messageError: error?.response?.data?.message,
      })
    }
  }

  const postSetRedButtonStatusAction = async (statusRedButton) => {
    try {
      setLoadingRequest(true)
      await postSetRedButtonStatus(tokenService, client, {
        event: {
          name: "set_red_button_status",
          data: {
            status: statusRedButton,
            user: user?.id,
          },
        },
      })
      onSuccess(setLoadingRequest)
    } catch (error) {
      setLoadingRequest(false)
      handleErrorSession({
        error: true,
        errorCode: error.response.status,
        messageError: error?.response?.data?.message,
      })
    }
  }

  const postHighPriorityTruckAction = async (orderIds = []) => {
    try {
      if (!orderIds?.length) return null
      setLoadingRequest(true)
      await postHighPriorityTruck(tokenService, client, {
        event: {
          name: "high_priority_trucks",
          data: {
            ids: orderIds,
          },
        },
      })
      onSuccess(setLoadingRequest)
    } catch (error) {
      setLoadingRequest(false)
      handleErrorSession({
        error: true,
        errorCode: error.response.status,
        messageError: error?.response?.data?.message,
      })
    }
  }

  const getTrucksAcceptOrderInfoAction = async () => {
    try {
      setLoadingRequest(true)
      const responseContacted = await postSearchEntity(
        "orders",
        {
          size: 1,
          filters: {
            "custom.acceptOrder": "TRUE",
            "currentStatus.tag": currentStatus
          },
        },
        tokenService
      )
      const responsePending = await postSearchEntity(
        "orders",
        {
          size: 1,
          filters: {
            "custom.acceptOrder": "pending",
            "currentStatus.tag": currentStatus
          },
        },
        tokenService
      )
      setAcceptOrdersInfo({
        contactedTrucks: responseContacted?.data?.message?.total,
        pendingContactedTrucks: responsePending?.data?.message?.total,
      })
      setLoadingRequest(false)
    } catch (error) {
      setLoadingRequest(false)
      handleErrorSession({
        error: true,
        errorCode: error.response.status,
        messageError: error?.response?.data?.message,
      })
    }
  }

  const getSummaryOrderInfoAction = async () => {
    try {
      setLoadingSummary(true)
      const bodyCounter = (status) => {
        return {
          size: 1,
          filters: {
            "currentStatus.tag": status
          },
        }
      }
      const responseCreated = await postSearchEntity(
        "orders",
        bodyCounter("CREATED"),
        tokenService
      )
      const responseReadyToGo = await postSearchEntity(
        "orders",
        bodyCounter("READY_TO_GO"),
        tokenService
      )
      const responseInTransit = await postSearchEntity(
        "orders",
        bodyCounter("IN_TRANSIT"),
        tokenService
      )
      const responseGateInComplete = await postSearchEntity(
        "orders",
        bodyCounter("GATEIN_COMPLETE"),
        tokenService
      )
      const responseClosed = await postSearchEntity(
        "orders",
        bodyCounter("CLOSED"),
        tokenService
      )
      setSummaryOrdersInfo({
        CREATED: responseCreated?.data?.message?.total,
        READY_TO_GO: responseReadyToGo?.data?.message?.total,
        IN_TRANSIT: responseInTransit?.data?.message?.total,
        GATEIN_COMPLETE: responseGateInComplete?.data?.message?.total,
        CLOSED: responseClosed?.data?.message?.total,
      })
      setLoadingSummary(false)
    } catch (error) {
      setLoadingSummary(false)
      handleErrorSession({
        error: true,
        errorCode: error.response.status,
        messageError: error?.response?.data?.message,
      })
    }
  }

  return {
    redButtonStatus,
    loadingRedButton,
    loadingRequest: (loadingRequest || loadingSummary),
    acceptOrdersInfo,
    summaryOrdersInfo,
    postHighPriorityTruckAction,
    postSetRedButtonStatusAction,
  }
}

export default usePrioritizationServices
